import { useEffect, useState } from 'react';

/**
 * Tracks browser connectivity so every admin/settings panel can render the
 * `offline` state the spec requires (§11) instead of showing a generic fetch
 * error the user cannot act on.
 *
 * `navigator.onLine === false` is a reliable "definitely offline" signal;
 * `true` only means "there is a network interface", so this hook is used to
 * explain failures, never to suppress a real error.
 *
 * @returns {boolean} whether the browser currently reports a connection.
 */
export default function useConnectionState() {
  const [online, setOnline] = useState(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine !== false
  );

  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  return online;
}
